import { invalidateRecordData } from '../state/serverCache';
import { statsOptOutHeaders } from './statsOptOut';

// Invenio API Service
// Handles all API calls to the InvenioRDM backend

const API_BASE = '/api';

interface MutationOptions { invalidate?: boolean }

async function recordMutationFetch(input: RequestInfo | URL, init: RequestInit, options: MutationOptions = {}): Promise<Response> {
  const response = await fetch(input, init);
  if (response.ok && options.invalidate !== false) invalidateRecordData();
  return response;
}


export type DoiFundingSource = 'cash' | 'credit' | 'quota';
export type DoiFulfillmentStatus = 'pending' | 'processing' | 'fulfilled' | 'failed';
export type DoiRefundStatus = 'none' | 'claimed' | 'refunded' | 'failed';

export type DoiCartEligibilityCode =
  | 'eligible'
  | 'already_in_cart'
  | 'not_a_draft'
  | 'published'
  | 'under_review'
  | 'existing_doi'
  | 'payment_active'
  | 'refund_processing'
  | 'refund_failed'
  | 'registered_institution'
  | 'cart_limit';

export interface DoiCartEligibility {
  eligible: boolean;
  code: DoiCartEligibilityCode;
  message: string;
}

export interface DoiCartItem {
  id: number;
  record_id: string;
  revision_id: number;
  current_revision_id?: number;
  title: string;
  selected: boolean;
  locked: boolean;
  institution_id?: string;
  institution_name?: string;
  intended_action?: 'direct_publish' | 'submit_review' | 'publisher_publish' | 'institution_publish';
  plagiarism_opt_in?: boolean;
  addon_amount_paise?: number;
  validation_errors: string[];
  eligibility?: DoiCartEligibility;
}

export interface DoiSuffixReservation {
  id: string;
  record_id?: string | null;
  doi?: string | null;
  status: 'pending' | 'awaiting_payment' | 'ready' | 'published' | 'expired' | 'failed';
  checkout_id?: string | null;
  retry_url?: string | null;
  payment_url?: string | null;
  needs_reconciliation?: boolean;
  created: string;
  updated: string;
  /** When the entitlement lapses; only set while it is still usable. */
  expires_at?: string | null;
  funding_source?: DoiFundingSource | null;
  institution_id?: string | null;
  institution_name?: string | null;
  /** Set only when an expired reservation needs a manual cash refund. */
  refund_contact?: string | null;
  complete_url?: string | null;
  record_url?: string | null;
}

/** One way a batch of reserved identifiers is being paid for. */
export interface ReservationFundingGroup {
  source: DoiFundingSource;
  institution_name: string;
  count: number;
  amount_paise: number;
  unit_amount_paise: number;
}

/**
 * Who a batch of reserved identifiers is billed to, and who else could be.
 *
 * `self` is the ordinary case: an individual pays for their own reservation
 * even when they belong to an institution, because reserving names no draft
 * and so carries nothing that says the institution asked for it.
 */
export interface ReservationPayer {
  mode: 'self' | 'institution' | 'publisher';
  /** The institution funding this, or the one that could have. */
  institution_name: string | null;
  /** Its balance — present only when this account may spend from it. */
  credits_available: number | null;
  can_spend_institution_credits: boolean;
}

/** What reserving a given number of identifiers costs, and from where. */
export interface ReservationQuote {
  quantity: number;
  limit: number;
  ttl_days: number;
  funding: ReservationFundingGroup[];
  payer: ReservationPayer;
  cash_amount_paise: number;
}

export interface DoiCart {
  id: string;
  status: string;
  item_count: number;
  selected_count: number;
  limit: number;
  can_bulk: boolean;
  guidance: 'shared_checkout_recommended' | 'bulkgen_recommended' | 'bulkgen_required' | 'checkout_required' | null;
  items: DoiCartItem[];
  reservation_limit?: number;
  reservation_ttl_days?: number;
  reservations?: DoiSuffixReservation[];
  /** The unpaid checkout currently holding this cart's locks, if any. */
  active_checkout?: DoiCartLock | null;
  /** Latest paid cart checkout, retained after its drafts leave the open cart. */
  recent_checkout?: DoiCheckout | null;
  checkout_lock_ttl_minutes?: number;
}

/**
 * Identifies the checkout blocking a locked cart, so the page can offer to
 * resume paying for it or release it rather than dead-ending.
 */
export interface DoiCartLock {
  id: string;
  status: string;
  item_count: number;
  cash_amount_paise: number;
  /** True once Razorpay has an order, so a real payment may be in flight. */
  payment_started: boolean;
  expires_at: string;
  expired: boolean;
}

export interface DoiCheckoutLine {
  id: number;
  record_id: string;
  title: string;
  institution_id?: string;
  institution_name?: string;
  intended_action: 'direct_publish' | 'submit_review' | 'publisher_publish' | 'institution_publish';
  funding_source: DoiFundingSource;
  doi_amount_paise: number;
  addon_amount_paise: number;
  line_amount_paise: number;
  plagiarism_opt_in: boolean;
  fulfillment_status: DoiFulfillmentStatus;
  fulfillment_error?: string;
  request_id?: string;
  refund_status: DoiRefundStatus;
  refund_id?: string;
  refunded_amount_paise: number;
  reservation_id?: string | null;
  reserved_doi?: string | null;
  complete_url?: string | null;
}

export interface DoiCheckout {
  id: string;
  cart_id?: string;
  source: 'cart' | 'bulkgen' | 'reservation';
  status: string;
  currency: string;
  cash_amount_paise: number;
  credit_count: number;
  quota_amount_paise: number;
  quota_count: number;
  quota_period_id?: string | null;
  razorpay_order_id?: string;
  razorpay_payment_id?: string;
  expires_at: string;
  lines: DoiCheckoutLine[];
}

export type QuotaPeriodStatus = 'open' | 'due' | 'overdue' | 'paid' | 'void';

export interface PublisherQuotaEntry {
  id: number;
  record_id: string;
  title: string;
  /** "plagiarism" lines are owner-ordered re-checks, billed at the addon price. */
  source: 'cart' | 'bulkgen' | 'plagiarism';
  checkout_id?: string | null;
  doi_amount_paise: number;
  addon_amount_paise: number;
  amount_paise: number;
  status: 'accrued' | 'reversed';
  created_at?: string | null;
}

export interface PublisherQuotaPeriod {
  id: string;
  period_start: string;
  period_end: string;
  label: string;
  status: QuotaPeriodStatus;
  currency: string;
  accrued_amount_paise: number;
  doi_count: number;
  due_at: string;
  suspend_at: string;
  paid_at?: string | null;
  razorpay_order_id?: string | null;
  razorpay_payment_id?: string | null;
  payable: boolean;
  days_until_suspension: number;
  entries?: PublisherQuotaEntry[];
}

export interface PublisherQuotaBlock {
  code: string;
  message: string;
  outstanding_amount_paise: number;
  outstanding_period_ids: string[];
  oldest_period_id?: string | null;
}

export interface PublisherQuotaAccount {
  enrolled: boolean;
  status: 'active' | 'suspended' | 'not_applicable';
  quota_enabled: boolean;
  suspend_grace_days?: number;
  current_period: PublisherQuotaPeriod | null;
  periods: PublisherQuotaPeriod[];
  has_older_periods?: boolean;
  history_next_before?: string | null;
  outstanding_amount_paise: number;
  block: PublisherQuotaBlock | null;
}

export interface PublisherQuotaHistoryPage {
  periods: PublisherQuotaPeriod[];
  has_more: boolean;
  next_before?: string | null;
}

/** Read the CSRF token from the Invenio REST double-submit cookie. */
export function getCsrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : '';
}

/** Build CSRF headers from the Invenio session cookie. */
export function getCsrfHeaders(): Record<string, string> {
  const token = getCsrfToken();
  if (!token) return {};
  return {
    'X-CSRFToken': token,
    'X-CSRF-Token': token,
  };
}

export type FieldErrors = Record<string, string>;

export class ApiValidationError extends Error {
  fieldErrors: FieldErrors;

  constructor(message: string, fieldErrors: FieldErrors) {
    super(message);
    this.name = 'ApiValidationError';
    this.fieldErrors = fieldErrors;
  }
}

function normalizeFieldErrors(payload: any): FieldErrors {
  if (!payload || typeof payload !== 'object' || !payload.errors) {
    return {};
  }

  const normalized: FieldErrors = {};
  if (Array.isArray(payload.errors)) {
    payload.errors.forEach((errorItem: any) => {
      if (!errorItem || typeof errorItem !== 'object') return;
      const field = typeof errorItem.field === 'string' ? errorItem.field.trim() : '';
      if (!field) return;
      const messages = Array.isArray(errorItem.messages)
        ? errorItem.messages
        : [errorItem.message || errorItem.messages || 'This field has an error.'];
      const message = messages.map((item: unknown) => String(item || '').trim()).filter(Boolean)[0];
      if (message) normalized[field] = message;
    });
    return normalized;
  }

  if (typeof payload.errors === 'object') {
    Object.entries(payload.errors).forEach(([field, messages]) => {
      const messageList = Array.isArray(messages) ? messages : [messages];
      const message = messageList
        .map((item: unknown) => String(item || '').trim())
        .filter(Boolean)[0];
      if (message) normalized[field] = message;
    });
  }

  return normalized;
}

export async function readErrorMessage(
  response: Response,
  fallbackMessage: string
): Promise<string> {
  const flattenMessage = (value: unknown, label = ''): string[] => {
    if (typeof value === 'string') {
      const message = value.trim();
      return message ? [label ? `${label}: ${message}` : message] : [];
    }
    if (Array.isArray(value)) {
      return value.flatMap(item => flattenMessage(item, label));
    }
    if (value && typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).flatMap(([field, item]) => {
        const nestedLabel = ['_schema', 'message', 'error'].includes(field)
          ? label
          : [label, field].filter(Boolean).join('.');
        return flattenMessage(item, nestedLabel);
      });
    }
    return [];
  };

  const formatPayloadMessage = (payload: any): string | null => {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    const errorMessages: string[] = [];

    if (Array.isArray(payload.errors)) {
      payload.errors.forEach((errorItem: any) => {
        if (!errorItem || typeof errorItem !== 'object') {
          return;
        }

        const field =
          typeof errorItem.field === 'string' && errorItem.field.trim()
            ? errorItem.field.trim()
            : '';

        let messages = errorItem.messages;
        if (typeof messages === 'string') {
          messages = [messages];
        }
        if (!Array.isArray(messages) || messages.length === 0) {
          messages = [errorItem.message || 'This field has an error.'];
        }

        messages.forEach((message: unknown) => {
          const text = String(message || '').trim();
          if (!text) return;
          errorMessages.push(field ? `${field}: ${text}` : text);
        });
      });
    }

    if (!Array.isArray(payload.errors)) {
      const fieldErrors = normalizeFieldErrors(payload);
      Object.entries(fieldErrors).forEach(([field, message]) => {
        errorMessages.push(`${field}: ${message}`);
      });
    }

    if (errorMessages.length > 0) {
      return errorMessages.join('\n');
    }

    if (Array.isArray(payload.message)) {
      const messages = payload.message
        .map((message: any) => String(message || '').trim())
        .filter(Boolean);
      if (messages.length > 0) {
        return messages.join('\n');
      }
    }

    if (typeof payload.message === 'string' && payload.message.trim()) {
      return payload.message.trim();
    }

    if (typeof payload.detail === 'string' && payload.detail.trim()) {
      return payload.detail.trim();
    }

    const nestedError = flattenMessage(payload.error);
    if (nestedError.length > 0) {
      return nestedError.join('\n');
    }

    return null;
  };

  try {
    const payload = await response.json();
    return formatPayloadMessage(payload) || fallbackMessage;
  } catch (_error) {
    // HTML proxy pages and unstructured exception bodies can contain server
    // internals. Only render the structured JSON shapes handled above.
    return fallbackMessage;
  }
}

/**
 * Throw the richest error the response supports.
 *
 * Invenio answers a rejected draft with `errors: [{field, messages}]`, which
 * names the exact field at fault. Flattening that into one string — as
 * `readErrorMessage` must, since it returns a string — is what left the upload
 * form able to say only "a validation error occurred" while the server had
 * already said which field and why.
 */
export async function throwApiError(
  response: Response,
  fallbackMessage: string
): Promise<never> {
  const cloned = response.clone();
  const message = await readErrorMessage(response, fallbackMessage);
  let fieldErrors: FieldErrors = {};
  try {
    fieldErrors = normalizeFieldErrors(await cloned.json());
  } catch (_error) {
    // A non-JSON body carries no field information; the message stands alone.
  }
  if (Object.keys(fieldErrors).length > 0) {
    throw new ApiValidationError(message, fieldErrors);
  }
  throw new Error(message);
}

// Types
export interface Creator {
  person_or_org: {
    name?: string;
    type: 'personal' | 'organizational';
    given_name?: string;
    family_name?: string;
    identifiers?: Array<{
      scheme: string;
      identifier: string;
    }>;
  };
  role?: {
    id: string;
  };
  affiliations?: Array<{
    name: string;
  }>;
}

export interface Title {
  title: string;
  type?: {
    id: string;
  };
  lang?: {
    id: string;
  };
}

export interface Description {
  description: string;
  type?: {
    id: string;
  };
  lang?: {
    id: string;
  };
}

export interface DateInfo {
  date: string;
  type?: {
    id: string;
  };
  description?: string;
}

export interface Subject {
  subject: string;
  scheme?: string;
  identifier?: string;
}

export interface Contributor {
  person_or_org: {
    name?: string;
    type: 'personal' | 'organizational';
    given_name?: string;
    family_name?: string;
    identifiers?: Array<{
      scheme: string;
      identifier: string;
    }>;
  };
  role: {
    id: string;
  };
  affiliations?: Array<{
    name: string;
  }>;
}

export interface Reference {
  reference: string;
  identifier?: string;
  scheme?: string;
}

export interface License {
  id: string;
  title?: string;
  url?: string;
}

export interface FileUpload {
  id: string;
  name: string;
  size: number;
  checksum?: string;
}

export interface DraftFileEntry {
  id: string;
  key: string;
  size: number;
  checksum?: string;
  status?: string;
  mimetype?: string;
  access?: {
    hidden?: boolean;
  };
}

export interface RecordMetadata {
  title: string;
  creators: Creator[];
  description?: string;
  publication_date?: string;
  resource_type?: {
    id: string;
  };
  publisher?: string;
  version?: string;
  languages?: Array<{ id: string }>;
  licenses?: License[];
  rights?: License[];
  subjects?: Subject[];
  contributors?: Contributor[];
  dates?: DateInfo[];
  related_identifiers?: Array<{
    identifier: string;
    // Optional: Invenio detects the scheme with idutils when it is omitted,
    // which is more reliable than guessing one its validator would reject.
    scheme?: string;
    relation_type: { id: string };
    resource_type?: { id: string };
  }>;
  identifiers?: Array<{
    scheme?: string;
    identifier?: string;
  }>;
  references?: Reference[];
  funding?: Array<{
    funder: { name: string };
    award?: { number: string; title?: string };
  }>;
}

export interface DraftData {
  metadata: RecordMetadata;
  files?: {
    enabled: boolean;
  };
  access?: {
    record: string;
    files: string;
    embargo?: {
      active: boolean;
      until?: string;
      reason?: string;
    };
  };
  custom_fields?: Record<string, any>;
}

export interface RecordUsageStats {
  views?: number;
  unique_views?: number;
  downloads?: number;
  unique_downloads?: number;
  data_volume?: number;
}

export interface RecordResponse {
  id: string;
  pid?: {
    doi?: {
      identifier: string;
      provider?: string;
      client?: string;
    };
    oai?: {
      identifier: string;
      provider?: string;
    };
  };
  /** Invenio API often returns pids (plural); we map to pid for display */
  pids?: {
    doi?: {
      identifier: string;
      provider?: string;
      client?: string;
    };
  };
  metadata: RecordMetadata;
  /**
   * Returned by Invenio on every record. `communities.default` is the community
   * that accepted the record, which is what landing-page branding resolves from
   * - see docs/feature-publisher-branding.md.
   */
  parent?: {
    id?: string;
    communities?: {
      ids?: string[];
      default?: string;
    };
  };
  links: {
    self: string;
    self_html: string;
    files?: string;
    publish?: string;
    review?: string;
  };
  status: 'draft' | 'published' | 'submitted_for_review';
  review_status?: string;
  is_published?: boolean;
  versions?: {
    index?: number;
    is_latest?: boolean;
  };
  created: string;
  updated: string;
  custom_fields?: Record<string, any>;
  /** Invenio usage statistics; absent until the stats processor has run. */
  stats?: {
    this_version?: RecordUsageStats;
    all_versions?: RecordUsageStats;
  };
  files?: {
    enabled: boolean;
    count?: number;
    total_bytes?: number;
    entries?: Record<string, DraftFileEntry>;
  };
  access?: {
    record: string;
    files: string;
    embargo?: {
      active: boolean;
      until?: string;
      reason?: string;
    };
  };
}

export interface SearchResult {
  hits: {
    hits: Array<{
      id: string;
      metadata: RecordMetadata;
      links: Record<string, string>;
      status?: string;
      is_published?: boolean;
      is_draft?: boolean;
      versions?: {
        index?: number;
        is_latest?: boolean;
      };
      custom_fields?: Record<string, any>;
    }>;
    total: number;
  };
  aggregations?: Record<string, any>;
}

/** Resolve the record lifecycle using Invenio's explicit publication truth. */
export function getRecordLifecycleStatus(
  hit: any
): RecordResponse['status'] {
  const explicitStatus = typeof hit?.status === 'string'
    ? hit.status.toLowerCase()
    : '';

  if (explicitStatus === 'in_review' || explicitStatus === 'submitted_for_review') {
    return 'submitted_for_review';
  }
  if (hit?.is_published === true) return 'published';
  if (hit?.is_published === false) return 'draft';
  if (explicitStatus === 'published') return 'published';
  if (
    explicitStatus === 'draft' ||
    explicitStatus === 'draft_with_review' ||
    explicitStatus === 'new_version_draft'
  ) {
    return 'draft';
  }

  const selfLink = String(hit?.links?.self || '').toLowerCase();
  return selfLink.includes('/draft') || hit?.is_draft === true
    ? 'draft'
    : 'published';
}

export interface DoiPaymentCheck {
  payment_required: boolean;
  credits_available: number;
  reason?: string | null;
  doi_cost_paise?: number;
  institution_id?: string;
  /** The signed-in account must check new DOIs out through the DOI cart. */
  checkout_required?: boolean;
  checkout_reason?: 'publisher_quota';
}

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  given_name: string;
  family_name: string;
  institution_name: string;
  contact_given_name: string;
  contact_family_name: string;
  is_organization: boolean;
  identity_edit_policy: IdentityEditPolicy | null;
  apaar_id: string;
  orcid_id: string;
  ror_id: string;
  aishe_code: string;
  phone: string;
  alternate_phone: string;
}

export interface IdentityEditPolicy {
  limit: number;
  window_days: number;
  edits_used: number;
  edits_remaining: number;
  next_edit_at: string | null;
}

export type UserProfileUpdate = Omit<
  UserProfile,
  'id' | 'email' | 'is_organization' | 'identity_edit_policy'
>;

// API Functions

/**
 * Create a new draft record
 */
export async function createDraft(data: DraftData): Promise<RecordResponse> {
  const response = await recordMutationFetch(`${API_BASE}/records`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await throwApiError(response, 'Failed to create draft');
  }

  return response.json();
}

/**
 * Get a draft by ID
 */
export async function getDraft(id: string): Promise<RecordResponse> {
  const response = await fetch(`${API_BASE}/records/${id}/draft`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to fetch draft'));
  }

  return response.json();
}

/**
 * Start editing a published record by creating its editable draft.
 * If a draft already exists (for example after a refresh), resume it.
 */
export async function createPublishedRecordDraft(id: string): Promise<RecordResponse> {
  const encodedId = encodeURIComponent(id);
  const response = await recordMutationFetch(`${API_BASE}/records/${encodedId}/draft`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
  });

  if (response.ok) {
    return response.json();
  }

  // Invenio returns a conflict when an edit draft already exists. Resuming it
  // makes refreshing or reopening the edit link safe and idempotent.
  if (response.status === 409) {
    return getDraft(id);
  }

  throw new Error(
    await readErrorMessage(response, 'Failed to start editing the published record')
  );
}

/**
 * Update a draft
 */
export async function updateDraft(id: string, data: Partial<DraftData>): Promise<RecordResponse> {
  const response = await recordMutationFetch(`${API_BASE}/records/${id}/draft`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await throwApiError(response, 'Failed to update draft');
  }

  return response.json();
}

/**
 * Publish a draft
 */
export async function publishDraft(id: string): Promise<RecordResponse> {
  const response = await recordMutationFetch(`${API_BASE}/records/${id}/draft/actions/publish`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    await throwApiError(response, 'Failed to publish');
  }

  return response.json();
}

export async function checkDoiPayment(params: {
  studentEmail: string;
  institutionId?: string;
}): Promise<DoiPaymentCheck> {
  const response = await fetch(`${API_BASE}/vedra/doi/check-payment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify({
      student_email: params.studentEmail,
      institution_id: params.institutionId || '',
    }),
  });

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, 'Unable to verify DOI payment requirements right now.')
    );
  }

  return response.json();
}

export async function getUserProfile(): Promise<UserProfile> {
  const response = await fetch(`${API_BASE}/vedra/profile`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load profile'));
  }

  const payload = await response.json();
  return payload.profile;
}

export async function updateUserProfile(profile: UserProfileUpdate): Promise<UserProfile> {
  const response = await fetch(`${API_BASE}/vedra/profile`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(profile),
  });

  if (!response.ok) {
    let payload: any = null;
    try {
      payload = await response.json();
    } catch (_error) {
      throw new Error('Failed to update profile');
    }

    const fieldErrors = normalizeFieldErrors(payload);
    if (Object.keys(fieldErrors).length > 0) {
      throw new ApiValidationError(
        typeof payload.message === 'string' ? payload.message : 'Profile validation failed',
        fieldErrors
      );
    }

    throw new Error(
      typeof payload.message === 'string' && payload.message.trim()
        ? payload.message.trim()
        : 'Failed to update profile'
    );
  }

  const payload = await response.json();
  return payload.profile;
}

export async function changeUserPassword(params: {
  current_password: string;
  new_password: string;
}): Promise<void> {
  const response = await fetch(`${API_BASE}/vedra/profile/password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to change password'));
  }
}

async function emailChangeRequest(path: string, body: object): Promise<any> {
  const response = await fetch(`${API_BASE}/vedra/me/email-change${path}`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await readErrorMessage(response, 'Unable to change email.'));
  return response.json();
}

export async function requestEmailChange(params: { new_email: string; current_password: string }): Promise<void> {
  await emailChangeRequest('', params);
}

export async function confirmEmailChange(token: string): Promise<UserProfile> {
  const result = await emailChangeRequest('/confirm', { token });
  return result.profile;
}

export async function getDoiCart(): Promise<DoiCart> {
  const response = await fetch(`${API_BASE}/vedra/doi/cart`, {
    credentials: 'include', cache: 'no-store',
  });
  if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to load DOI cart'));
  return response.json();
}

export async function addDoiCartItem(recordId: string): Promise<DoiCart> {
  const response = await fetch(`${API_BASE}/vedra/doi/cart/items`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
    body: JSON.stringify({ record_id: recordId }),
  });
  if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to add draft to DOI cart'));
  return response.json();
}

export async function getDoiCartEligibility(
  recordIds: string[]
): Promise<Record<string, DoiCartEligibility>> {
  if (recordIds.length === 0) return {};
  const response = await fetch(`${API_BASE}/vedra/doi/cart/eligibility`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
    body: JSON.stringify({ record_ids: recordIds }),
  });
  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, 'Failed to check DOI cart eligibility')
    );
  }
  const payload = await response.json();
  return payload.items || {};
}

export async function selectDoiCartItem(itemId: number, selected: boolean): Promise<DoiCart> {
  const response = await fetch(`${API_BASE}/vedra/doi/cart/items/${itemId}`, {
    method: 'PATCH', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
    body: JSON.stringify({ selected }),
  });
  if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to update cart'));
  return response.json();
}

export async function removeDoiCartItem(itemId: number): Promise<DoiCart> {
  const response = await fetch(`${API_BASE}/vedra/doi/cart/items/${itemId}`, {
    method: 'DELETE', credentials: 'include', headers: getCsrfHeaders(),
  });
  if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to remove cart item'));
  return response.json();
}

export async function createDoiCheckout(params: {
  source?: 'cart' | 'bulkgen' | 'reservation'; record_ids?: string[]; quantity?: number;
} = {}): Promise<DoiCheckout> {
  const response = await recordMutationFetch(`${API_BASE}/vedra/doi/checkouts`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
    body: JSON.stringify(params),
  });
  if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to create checkout'));
  return response.json();
}

/**
 * What one plagiarism check costs this account right now.
 *
 * Always ask the server. The rate depends on account type and on the
 * institution a deposit is going to, and every place that used to guess it in
 * the browser eventually disagreed with what was actually charged.
 */
export async function getPlagiarismPrice(
  institutionId?: string
): Promise<{ price_paise: number; institution_id: string | null }> {
  const query = institutionId
    ? `?institution_id=${encodeURIComponent(institutionId)}`
    : '';
  const response = await fetch(`${API_BASE}/vedra/plagiarism/price${query}`, {
    credentials: 'include', cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load the check price'));
  }
  return response.json();
}

/**
 * Ask what reserving this many identifiers would cost, and what pays for it.
 *
 * The account already answers that -- a publisher's statement, an
 * institution's credits, or a card -- so the person is shown the answer
 * instead of being asked the question.
 */
export async function getReservationQuote(quantity: number): Promise<ReservationQuote> {
  const response = await fetch(
    `${API_BASE}/vedra/doi/reservations/quote?quantity=${encodeURIComponent(quantity)}`,
    { credentials: 'include', cache: 'no-store' },
  );
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Could not price this reservation'));
  }
  return response.json();
}

/** Reserved identifiers this account owns but has not finished yet. */
export async function getDoiReservations(): Promise<DoiSuffixReservation[]> {
  const cart = await getDoiCart();
  return cart.reservations || [];
}

/** Load the signed-in publisher's monthly quota statements. */
export async function getPublisherQuota(): Promise<PublisherQuotaAccount> {
  const response = await fetch(`${API_BASE}/vedra/doi/quota`, {
    credentials: 'include', cache: 'no-store',
  });
  if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to load payments'));
  return response.json();
}

/** Load one statement together with its accrued line items. */
export async function getPublisherQuotaPeriod(periodId: string): Promise<PublisherQuotaPeriod> {
  const response = await fetch(`${API_BASE}/vedra/doi/quota/periods/${periodId}`, {
    credentials: 'include', cache: 'no-store',
  });
  if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to load statement'));
  return response.json();
}

/** Load the next page of older statement summaries. */
export async function getPublisherQuotaHistory(
  before?: string | null,
  limit = 12
): Promise<PublisherQuotaHistoryPage> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (before) params.set('before', before);
  const response = await fetch(`${API_BASE}/vedra/doi/quota/history?${params}`, {
    credentials: 'include', cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load older statements'));
  }
  return response.json();
}

/** Where a membership sits in its expiry lifecycle. */
export type MembershipPhase =
  | 'active'
  | 'renewal_notice'
  | 'final_week'
  | 'expired_grace'
  | 'halted';

export interface MembershipStatus {
  account_id: string | null;
  /** False for accounts with no membership at all — nothing to renew. */
  enrolled: boolean;
  active: boolean;
  validity_end: string | null;
  /** Legacy alias of validity_end, kept for older callers. */
  expiry_date: string | null;
  days_until_expiry: number | null;
  days_since_expiry: number | null;
  phase: MembershipPhase;
  tone: 'info' | 'warning' | 'critical' | null;
  show_header_bar: boolean;
  halt_on: string | null;
  days_until_halt: number | null;
  grace_days: number | null;
  /** True once DOI generation has actually been paused. */
  blocked: boolean;
  headline: string;
  renewal_url: string;
  membership_id: string | null;
  warnings: string[];
}

/**
 * Load the signed-in account's own membership expiry status.
 *
 * Never cached: the countdown and the halt both turn over at a date boundary,
 * and a stale copy would tell a member their services are fine after they have
 * stopped.
 */
export async function getMembershipStatus(): Promise<MembershipStatus> {
  const response = await fetch(`${API_BASE}/vedra/membership/status`, {
    credentials: 'include', cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load membership status'));
  }
  return response.json();
}

/** What renewing costs this member, before a quote is taken. */
export interface MembershipRenewalOptions {
  /** False for accounts with no membership — nothing to renew. */
  enrolled: boolean;
  membershipId?: string;
  membershipPublicId?: string;
  membershipType?: string;
  membershipLabel?: string;
  validityEnd?: string | null;
  /** The end date the member would hold after renewing. */
  newValidityEnd?: string;
  phase?: MembershipPhase;
  halted?: boolean;
  /** False when the plan cannot be priced automatically — renew via support. */
  renewable?: boolean;
  unavailableReason?: string | null;
  baseAmountPaise?: number;
  addonAvailable?: boolean;
  addonAmountPaise?: number;
  addonLabel?: string;
  addonFreeChecks?: string;
  /** Pre-ticks the add-on box for a member who already had it. */
  addonHeldPreviously?: boolean;
  currency?: string;
}

/** The server-authoritative quote a renewal payment is checked against. */
export interface MembershipRenewalQuote {
  id: string;
  membership_id: string;
  membership_public_id?: string | null;
  membership_type: string;
  membership_label: string;
  base_amount_paise: number;
  addon_amount_paise: number;
  addon_included: boolean;
  amount_paise: number;
  currency: string;
  status: 'quoted' | 'order_created' | 'paid';
  razorpay_order_id: string | null;
  razorpay_payment_id: string | null;
  previous_validity_end: string | null;
  new_validity_end: string | null;
  paid_at: string | null;
  sheet_status: string;
}

/** Load the renewal price and options for the signed-in member. */
export async function getMembershipRenewalOptions(): Promise<MembershipRenewalOptions> {
  const response = await fetch(`${API_BASE}/vedra/membership/renewal/options`, {
    credentials: 'include', cache: 'no-store',
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load renewal options'));
  }
  return response.json();
}

/**
 * Take the authoritative quote. The amount is decided server-side from the
 * member's plan — the browser only says whether to carry the add-on over.
 */
export async function createMembershipRenewalQuote(
  includeAddon: boolean
): Promise<MembershipRenewalQuote> {
  const response = await fetch(`${API_BASE}/vedra/membership/renewal/quote`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...getCsrfHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ includeAddon }),
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to prepare your renewal'));
  }
  return response.json();
}

export async function getDoiCheckout(checkoutId: string): Promise<DoiCheckout> {
  const response = await fetch(`${API_BASE}/vedra/doi/checkouts/${checkoutId}`, {
    credentials: 'include', cache: 'no-store',
  });
  if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to load checkout'));
  return response.json();
}

export async function abandonDoiCheckout(checkoutId: string): Promise<DoiCheckout> {
  const response = await recordMutationFetch(`${API_BASE}/vedra/doi/checkouts/${checkoutId}/abandon`, {
    method: 'POST', credentials: 'include', headers: getCsrfHeaders(),
  });
  if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to abandon checkout'));
  return response.json();
}

export async function retryDoiCheckout(checkoutId: string): Promise<void> {
  const response = await recordMutationFetch(`${API_BASE}/vedra/doi/checkouts/${checkoutId}/retry`, {
    method: 'POST', credentials: 'include', headers: getCsrfHeaders(),
  });
  if (!response.ok) throw new Error(await readErrorMessage(response, 'Failed to retry fulfillment'));
}

export async function createCommunityReview(
  recordId: string,
  institutionId: string
): Promise<unknown> {
  const response = await recordMutationFetch(`${API_BASE}/records/${recordId}/draft/review`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify({
      type: 'community-submission',
      receiver: {
        community: institutionId,
      },
    }),
  });

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, 'Failed to create institution review request')
    );
  }

  return response.json();
}

export async function submitCommunityReview(recordId: string): Promise<unknown> {
  const response = await recordMutationFetch(`${API_BASE}/records/${recordId}/draft/actions/submit-review`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, 'Failed to submit institution review request')
    );
  }

  return response.json();
}

/**
 * Get user's drafts
 */
export async function getUserDrafts(params?: {
  page?: number;
  size?: number;
  sort?: string;
}): Promise<SearchResult> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', params.page.toString());
  if (params?.size) queryParams.set('size', params.size.toString());
  if (params?.sort) queryParams.set('sort', params.sort);
  queryParams.set('shared_with_me', 'false');

  const response = await fetch(`${API_BASE}/user/records?${queryParams.toString()}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch drafts');
  }

  return response.json();
}

/**
 * Get user's published records
 */
export async function getUserRecords(params?: {
  page?: number;
  size?: number;
  sort?: string;
  q?: string;
}): Promise<SearchResult> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', params.page.toString());
  if (params?.size) queryParams.set('size', params.size.toString());
  if (params?.sort) queryParams.set('sort', params.sort);
  if (params?.q) queryParams.set('q', params.q);

  const response = await fetch(`${API_BASE}/user/records?${queryParams.toString()}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch records');
  }

  return response.json();
}

/**
 * Delete a draft
 */
export async function deleteDraft(id: string, options: MutationOptions = {}): Promise<void> {
  const response = await recordMutationFetch(`${API_BASE}/records/${id}/draft`, {
    method: 'DELETE',
    headers: getCsrfHeaders(),
    credentials: 'include',
  }, options);

  if (!response.ok) {
    throw new Error('Failed to delete draft');
  }
}

/**
 * Upload a file to a draft
 */
export type UploadPhase = 'preparing' | 'uploading' | 'finalizing';
export interface UploadFileOptions extends MutationOptions {
  onProgress?: (progress: number) => void;
  onPhase?: (phase: UploadPhase) => void;
  signal?: AbortSignal;
  serverEntry?: DraftFileEntry;
}

const abortError = () => new DOMException('The upload was cancelled.', 'AbortError');

function uploadFileContent(recordId: string, file: File, options: UploadFileOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    if (options.signal?.aborted) { reject(abortError()); return; }
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    const cleanup = () => options.signal?.removeEventListener('abort', abort);
    xhr.open('PUT', `${API_BASE}/records/${recordId}/draft/files/${encodeURIComponent(file.name)}/content`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    Object.entries(getCsrfHeaders()).forEach(([key, value]) => xhr.setRequestHeader(key, value));
    xhr.upload.onprogress = event => {
      if (event.lengthComputable && event.total > 0) options.onProgress?.(Math.min(100, event.loaded / event.total * 100));
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(xhr.responseText || `Failed to upload file (${xhr.status})`));
    };
    xhr.onerror = () => { cleanup(); reject(new Error('Failed to upload file')); };
    xhr.onabort = () => { cleanup(); reject(abortError()); };
    options.signal?.addEventListener('abort', abort, { once: true });
    xhr.send(file);
  });
}

export async function uploadFile(
  recordId: string,
  file: File,
  onProgressOrOptions?: ((progress: number) => void) | UploadFileOptions
): Promise<FileUpload> {
  const options: UploadFileOptions = typeof onProgressOrOptions === 'function'
    ? { onProgress: onProgressOrOptions }
    : (onProgressOrOptions || {});
  if (options.signal?.aborted) throw abortError();
  const serverCompleted = options.serverEntry?.status === 'completed'
    && options.serverEntry.key === file.name
    && options.serverEntry.size === file.size;
  if (serverCompleted) return { id: file.name, name: file.name, size: file.size, checksum: options.serverEntry?.checksum };

  // Initialize file upload
  if (!options.serverEntry) {
    options.onPhase?.('preparing');
    const initResponse = await recordMutationFetch(`${API_BASE}/records/${recordId}/draft/files`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
    signal: options.signal,
    body: JSON.stringify([{ key: file.name }]),
    }, { invalidate: false });

    if (!initResponse.ok) {
      throw new Error(await readErrorMessage(initResponse, 'Failed to initialize file upload'));
    }
  }

  // Upload file content
  options.onPhase?.('uploading');
  await uploadFileContent(recordId, file, options);

  // Commit the file
  options.onPhase?.('finalizing');
  const commitResponse = await recordMutationFetch(`${API_BASE}/records/${recordId}/draft/files/${encodeURIComponent(file.name)}/commit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
    signal: options.signal,
  }, options);

  if (!commitResponse.ok) {
    throw new Error(
      await readErrorMessage(commitResponse, 'Failed to commit file')
    );
  }

  return {
    id: file.name,
    name: file.name,
    size: file.size,
  };
}

/**
 * Delete a file from a draft
 */
export async function deleteFile(recordId: string, filename: string): Promise<void> {
  const response = await recordMutationFetch(
    `${API_BASE}/records/${recordId}/draft/files/${encodeURIComponent(filename)}`,
    {
      method: 'DELETE',
      headers: getCsrfHeaders(),
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to delete file'));
  }
}

/**
 * Get a published record
 */
export async function getRecord(
  id: string,
  options: { countsAsView?: boolean } = {}
): Promise<RecordResponse> {
  // Invenio raises a view event on every read of a record. A caller that is
  // not a member of the public opening the record page passes
  // countsAsView: false so the read is not counted.
  const { countsAsView = true } = options;
  const response = await fetch(`${API_BASE}/records/${id}`, {
    method: 'GET',
    credentials: 'include',
    headers: countsAsView ? undefined : statsOptOutHeaders(),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch record');
  }

  return response.json();
}

/**
 * Search records
 */
export async function searchRecords(params: {
  q?: string;
  page?: number;
  size?: number;
  sort?: string;
  resource_type?: string;
}): Promise<SearchResult> {
  const queryParams = new URLSearchParams();
  if (params.q) queryParams.set('q', params.q);
  if (params.page) queryParams.set('page', params.page.toString());
  if (params.size) queryParams.set('size', params.size.toString());
  if (params.sort) queryParams.set('sort', params.sort);

  const response = await fetch(`${API_BASE}/records?${queryParams.toString()}`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to search records');
  }

  return response.json();
}

// Resource type mapping for frontend labels
export const RESOURCE_TYPES = {
  'publication-thesis': { id: 'publication-thesis', label: 'Thesis' },
  'publication-dissertation': { id: 'publication-dissertation', label: 'Dissertation' },
  'publication-article': { id: 'publication-article', label: 'Research Paper' },
  'publication-book-chapter': { id: 'publication-book-chapter', label: 'Book Chapter' },
  'publication-ug-project': { id: 'publication-report', label: 'UG Project', customLabel: 'UG Project' },
  'publication-pg-project': { id: 'publication-report', label: 'PG Project', customLabel: 'PG Project' },
  'image-photo': { id: 'image-photo', label: 'Photo' },
  'dataset': { id: 'dataset', label: 'Dataset' },
  'software': { id: 'software', label: 'Software' },
};

// ---------------------------------------------------------------------------
// Review Requests (community-submission)
// ---------------------------------------------------------------------------

export interface CommunitySubmissionRequest {
  id: string;
  type: string;
  status: string;
  title: string;
  description?: string;
  receiver: { community: string };
  topic: { record: string };
  created: string;
  updated: string;
  links: {
    self: string;
    actions?: { accept?: string; decline?: string };
  };
  expanded?: {
    receiver?: {
      community?: { id?: string; metadata?: { title?: string } };
    };
    created_by?: {
      user?: {
        profile?: { full_name?: string };
        email?: string;
      };
    };
    topic?: {
      record?: {
        metadata?: {
          title?: any;
          description?: any;
          creators?: any[];
          resource_type?: { id?: string };
        };
      };
    };
  };
}

export interface RequestsSearchResult {
  hits: {
    hits: CommunitySubmissionRequest[];
    total: number;
  };
}

export type InstitutionReviewFilter = 'all' | 'pending' | 'accepted' | 'declined';

export async function getInstitutionReviews(
  status: InstitutionReviewFilter = 'pending',
  page = 1,
  size = 100
): Promise<RequestsSearchResult> {
  const params = new URLSearchParams({
    status,
    page: String(page),
    size: String(size),
  });
  const response = await fetch(`${API_BASE}/vedra/reviews?${params}`, {
    method: 'GET',
    credentials: 'include',
    headers: { ...getCsrfHeaders() },
  });

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, 'Failed to load review requests')
    );
  }

  return response.json();
}

export async function getPendingReviewRequests(): Promise<RequestsSearchResult> {
  return getInstitutionReviews('pending', 1, 100);
}

export async function getReviewPlagiarismReport<T = unknown>(
  requestId: string
): Promise<T | null> {
  const response = await fetch(
    `${API_BASE}/vedra/reviews/${requestId}/plagiarism`,
    {
      method: 'GET',
      credentials: 'include',
      headers: { ...getCsrfHeaders() },
    }
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, 'Failed to load plagiarism report')
    );
  }
  return response.json();
}

export async function acceptReviewRequest(
  requestId: string,
  actionUrl?: string
): Promise<unknown> {
  const url =
    actionUrl || `${API_BASE}/requests/${requestId}/actions/accept`;
  const response = await recordMutationFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, 'Failed to accept review request')
    );
  }

  return response.json();
}

/**
 * Accept a community-submission review AND tag the record with the submitter
 * type (student/faculty/other). Hits the Vedra wrapper endpoint which sets
 * `custom_fields.vedra_submitter_type` on the draft before triggering the
 * standard accept flow (which runs DOI payment/credit checks).
 *
 * Use this in favor of `acceptReviewRequest` whenever the caller has a
 * submitter_type to set — which is every approval path going forward.
 */
export async function acceptReviewRequestWithSubmitterType(
  requestId: string,
  submitterType: 'student' | 'faculty' | 'other'
): Promise<unknown> {
  const url = `${API_BASE}/vedra/reviews/${requestId}/accept`;
  const response = await recordMutationFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify({ submitter_type: submitterType }),
  });

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, 'Failed to accept review request')
    );
  }

  return response.json();
}

export async function declineReviewRequest(
  requestId: string,
  actionUrl?: string,
  reason?: string
): Promise<unknown> {
  const url =
    actionUrl || `${API_BASE}/requests/${requestId}/actions/decline`;
  const response = await recordMutationFetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(reason ? { payload: { content: reason } } : {}),
  });

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, 'Failed to decline review request')
    );
  }

  return response.json();
}

/**
 * Get a single request by ID
 */
export async function getRequest(requestId: string): Promise<CommunitySubmissionRequest> {
  const response = await fetch(`${API_BASE}/requests/${requestId}`, {
    method: 'GET',
    credentials: 'include',
    headers: { ...getCsrfHeaders() },
  });

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, 'Failed to fetch request')
    );
  }

  return response.json();
}

/**
 * Get the current user's community-submission requests (all statuses)
 */
export async function getUserSubmissions(): Promise<RequestsSearchResult> {
  const params = new URLSearchParams({
    q: 'type:community-submission',
    size: '100',
  });
  const response = await fetch(`${API_BASE}/requests?${params}`, {
    method: 'GET',
    credentials: 'include',
    headers: { ...getCsrfHeaders() },
  });

  if (!response.ok) {
    throw new Error(
      await readErrorMessage(response, 'Failed to load submissions')
    );
  }

  return response.json();
}

export interface CreatorRoleOption {
  id: string;
  label: string;
}

export function creatorRoleToMetadata(role?: string): Creator['role'] {
  const id = role?.trim();
  return id ? { id } : undefined;
}

/** An entry in one of Invenio's controlled vocabularies. */
export type VocabularyOption = CreatorRoleOption;

interface VocabularyRecord {
  id?: unknown;
  title?: unknown;
}

interface VocabularyResponse {
  hits?: {
    hits?: VocabularyRecord[];
  };
}

/**
 * Load a controlled vocabulary, keeping only entries we can label.
 *
 * Invenio ships these as records with a localised title; we show the English
 * one and fall back to the identifier so an untranslated entry is still
 * selectable rather than blank.
 */
async function getVocabularyOptions(
  vocabulary: string,
  failureMessage: string
): Promise<VocabularyOption[]> {
  const response = await fetch(
    `${API_BASE}/vocabularies/${vocabulary}?size=100`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(await readErrorMessage(response, failureMessage));
  }

  const payload = await response.json() as VocabularyResponse;
  const vocabularyHits = payload.hits?.hits;
  const records = Array.isArray(vocabularyHits) ? vocabularyHits : [];

  return records.flatMap((record) => {
    if (typeof record.id !== 'string' || !record.id.trim()) {
      return [];
    }

    const id = record.id;
    const englishTitle =
      record.title &&
      typeof record.title === 'object' &&
      !Array.isArray(record.title) &&
      typeof (record.title as Record<string, unknown>).en === 'string'
        ? ((record.title as Record<string, unknown>).en as string).trim()
        : '';

    return [{ id, label: englishTitle || id }];
  });
}

/**
 * Load qualified creator roles from Invenio's vocabulary.
 *
 * The synthetic "Default" choice is intentionally not returned here because
 * it represents an omitted role in record metadata, not a vocabulary entry.
 */
export async function getCreatorRoles(): Promise<CreatorRoleOption[]> {
  return getVocabularyOptions('creatorsroles', 'Failed to load creator roles');
}

/**
 * Load the DataCite relation types a related work can be linked by.
 *
 * These are what turns a bare identifier into a real citation link: "Cites",
 * "Is supplement to", "Is new version of", and so on.
 */
export async function getRelationTypes(): Promise<VocabularyOption[]> {
  return getVocabularyOptions('relationtypes', 'Failed to load relation types');
}

/**
 * Load the resource types a related work can be described as.
 *
 * This is the optional half of a DataCite relation: it says whether the work
 * on the other end is an article, a dataset, software, and so on.
 */
export async function getResourceTypes(): Promise<VocabularyOption[]> {
  return getVocabularyOptions('resourcetypes', 'Failed to load resource types');
}

// Languages
export const LANGUAGES = [
  { id: 'eng', label: 'English' },
  { id: 'hin', label: 'Hindi' },
  { id: 'deu', label: 'German' },
  { id: 'fra', label: 'French' },
  { id: 'spa', label: 'Spanish' },
];

// =============================================================================
// Bulk Accept (institution admin batch-approve)
// =============================================================================

export interface BulkAcceptJobResult {
  request_id: string;
  status: 'ok' | 'failed';
  error?: string;
}

export interface BulkAcceptJob {
  id: string;
  status: 'queued' | 'processing' | 'completed' | 'completed_with_errors' | 'failed';
  progress: number;
  total: number;
  succeeded?: number;
  failed?: number;
  submitter_type: 'student' | 'faculty' | 'other';
  results?: BulkAcceptJobResult[];
}

export async function bulkAcceptReviews(
  requestIds: string[],
  submitterType: 'student' | 'faculty' | 'other',
): Promise<{ job_id: string; status_url: string; total: number }> {
  const response = await fetch(`${API_BASE}/vedra/reviews/bulk-accept`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify({ request_ids: requestIds, submitter_type: submitterType }),
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to start bulk approve'));
  }
  return response.json();
}

export async function getBulkAcceptJobStatus(jobId: string): Promise<BulkAcceptJob> {
  const response = await fetch(`${API_BASE}/vedra/reviews/bulk-jobs/${jobId}`, {
    credentials: 'include',
    headers: getCsrfHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch bulk-accept job ${jobId}`);
  }
  return response.json();
}

// ---------------------------------------------------------------------------
// Document versions and plagiarism re-checks
// ---------------------------------------------------------------------------

export type PlagiarismReportTrigger = 'review' | 'rerun' | 'doi_purchase';

export interface PlagiarismReportSummary {
  id: number;
  trigger: PlagiarismReportTrigger;
  request_id: string | null;
  /** Document version the check examined. */
  document_version: number;
  is_current: boolean;
  in_progress: boolean;
  status: string | null;
  error?: string | null;
  files: Array<{
    filename: string;
    status: string;
    similarity_pct?: string;
    ai_pct?: string;
    error?: string;
  }>;
  queued_at?: string | null;
  completed_at?: string | null;
  created: string | null;
  funding?: 'razorpay' | 'quota' | 'doi_cart' | null;
}

export type PlagiarismRerunReason =
  | 'updated_checks_coming_soon'
  | 'never_checked'
  | 'document_changed'
  | 'previous_failed'
  | 'not_published'
  | 'not_authorized'
  | 'check_in_progress'
  | 'report_current';

export interface PlagiarismRerunAvailability {
  available: boolean;
  reason: PlagiarismRerunReason | null;
  price_paise: number;
  currency: string;
  current_version: number;
  report_version: number | null;
}

export interface RecordPlagiarismStatus {
  record_id: string;
  document_version: number;
  document_version_at: string | null;
  files: Array<{ key: string; size?: number; checksum?: string; mimetype?: string }>;
  reports: PlagiarismReportSummary[];
  active_report: PlagiarismReportSummary | null;
  /** The report people would rely on examined an older document version. */
  stale: boolean;
  rerun: PlagiarismRerunAvailability;
}

export interface PlagiarismRecordSummary {
  document_version: number;
  report_version: number | null;
  report_status: string | null;
  in_progress: boolean;
  stale: boolean;
  rerun_available: boolean;
}

/** The server wants Razorpay payment before it queues the re-check. */
export class PlagiarismPaymentRequiredError extends Error {
  pricePaise: number;

  constructor(pricePaise: number) {
    super('Payment required for the plagiarism re-check');
    this.name = 'PlagiarismPaymentRequiredError';
    this.pricePaise = pricePaise;
  }
}

export async function getRecordPlagiarismStatus(
  recordId: string
): Promise<RecordPlagiarismStatus | null> {
  const response = await fetch(
    `${API_BASE}/vedra/plagiarism/${encodeURIComponent(recordId)}/status`,
    { credentials: 'include', headers: { ...getCsrfHeaders() } }
  );
  // 401/403/404: not the owner, or not a published record - nothing to show.
  if (response.status === 401 || response.status === 403 || response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load plagiarism status'));
  }
  return response.json();
}

/**
 * Order a re-check. Publishers are billed on their monthly statement and the
 * check queues at once; anyone else gets PlagiarismPaymentRequiredError and
 * pays through PlagiarismRerunModal, which reaches Invenio via FastAPI.
 */
export async function requestPlagiarismRerun(
  recordId: string
): Promise<{ queued: boolean; funding: string; report: PlagiarismReportSummary; status: RecordPlagiarismStatus }> {
  const response = await recordMutationFetch(
    `${API_BASE}/vedra/plagiarism/${encodeURIComponent(recordId)}/rerun`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...getCsrfHeaders() },
    }
  );
  if (response.status === 402) {
    const payload = await response.json().catch(() => ({}));
    throw new PlagiarismPaymentRequiredError(Number(payload?.price_paise) || 0);
  }
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to order the plagiarism re-check'));
  }
  return response.json();
}

export async function getPlagiarismSummaries(
  recordIds: string[]
): Promise<Record<string, PlagiarismRecordSummary>> {
  const ids = recordIds.filter(Boolean).slice(0, 50);
  if (ids.length === 0) return {};
  const params = new URLSearchParams({ record_ids: ids.join(',') });
  const response = await fetch(`${API_BASE}/vedra/plagiarism/summary?${params}`, {
    credentials: 'include',
    headers: { ...getCsrfHeaders() },
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Failed to load plagiarism summaries'));
  }
  const payload = await response.json();
  return payload && typeof payload.summaries === 'object' && payload.summaries
    ? payload.summaries
    : {};
}
